import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Landed here from AWS Marketplace fulfillment (see
// S:/Git/vaia-marketplace-entitlements). ?token=... identifies the AWS
// customer this account gets linked to. There's no other self-service
// signup page in this app (accounts are normally admin-provisioned via
// AdminClientsProspects -> /auth/register), so this exists solely for the
// Marketplace flow.
const MarketplaceActivate = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setInfoError('This activation link is missing a token.');
      return;
    }
    axios.get(`${API}/marketplace/activation-info`, { params: { token } })
      .then((res) => setInfo(res.data))
      .catch((err) => setInfoError(err.response?.data?.detail || 'This activation link is invalid or has expired.'));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/marketplace/activate`, { token, ...form });
      toast.success('Account created — welcome to VAIA Wealth!');
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create your account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-white via-white to-[#fffdf0]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Set Up Your Account</h2>
            <p className="text-gray-600 text-sm">Finish setting up your VAIA Wealth subscription</p>
          </div>

          {infoError ? (
            <p className="text-red-600 text-sm">{infoError} Contact foundersoffice@vaia.co.in for help.</p>
          ) : !info ? (
            <p className="text-gray-500 text-sm">Loading…</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input id="full_name" required value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <Button
                type="submit"
                className="w-full mt-2 bg-gradient-to-r from-ugmonk-yellow to-[#f5cc6a] hover:from-[#f5cc6a] hover:to-ugmonk-yellow text-ugmonk-dark font-medium"
                disabled={loading}
              >
                {loading ? 'Creating account…' : 'Create Account'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceActivate;
